import { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Loader2, CheckCircle2, AlertCircle, X, Image, Clock, ChevronDown, Settings2, FolderTree, Layers } from 'lucide-react';
import { sitemapApi, domainApi, type GenerateSitemapResponse, type ImageEntry, type SitemapSizeTier, type SegmentationStrategy, type SegmentationConfig, type SegmentationPreviewResponse } from '@/lib/api';
import { cn, formatNumber } from '@/lib/utils';
import {
  HreflangManager,
  loadHreflangClusters,
  buildHreflangIndex,
  type HreflangCluster,
  type HreflangEntry,
} from '@/components/Hreflang/HreflangManager';
import {
  ImageMetadataManager,
  loadImageMetadata,
  buildImagesIndex,
  type ImageMetadataEntry,
} from '@/components/ImageMetadata/ImageMetadataManager';

const SITEMAP_SIZE_OPTIONS: { value: SitemapSizeTier; label: string; description: string; urlsPerSitemap: number }[] = [
  { value: 'standard', label: 'Standard (Recommended)', description: 'Best for most sites', urlsPerSitemap: 50000 },
  { value: 'balanced', label: 'Balanced', description: 'More granular GSC insights', urlsPerSitemap: 10000 },
  { value: 'gsc-optimized', label: 'GSC Optimized', description: 'Matches GSC 1k row export limit', urlsPerSitemap: 1000 },
];

const SEGMENTATION_OPTIONS: { value: SegmentationStrategy; label: string; description: string; icon: typeof Layers }[] = [
  { value: 'sequential', label: 'Sequential', description: 'Split into equal chunks', icon: Layers },
  { value: 'directory', label: 'Directory-based', description: 'Group by URL path', icon: FolderTree },
];

interface FileManagerProps {
  domain: string;
  domainId: string;
  onSuccess?: (result: GenerateSitemapResponse) => void;
  prepareMode?: boolean;
}

type InputMode = 'file' | 'text';

interface UrlEntry {
  loc: string;
  lastmod?: string;
  changefreq?: string;
  priority?: number;
  images?: ImageEntry[];
  hreflang?: HreflangEntry[];
}

export function FileManager({ domain, domainId, onSuccess, prepareMode = false }: FileManagerProps) {
  const [mode, setMode] = useState<InputMode>('file');
  const [textInput, setTextInput] = useState('');
  const [parsedUrls, setParsedUrls] = useState<UrlEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateSitemapResponse | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [sitemapSize, setSitemapSize] = useState<SitemapSizeTier>('standard');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Hreflang clusters + image metadata are domain-scoped. We hydrate from
  // D1 on mount (so edits sync across browsers and devices) and fall back
  // to localStorage if the API call fails. localStorage stays as a
  // write-through cache so offline edits aren't lost.
  const [hreflangClusters, setHreflangClusters] = useState<HreflangCluster[]>(() =>
    loadHreflangClusters(domain)
  );
  const [imageEntries, setImageEntries] = useState<ImageMetadataEntry[]>(() =>
    loadImageMetadata(domain)
  );

  // Hydrate on mount + when the domain switches. The components themselves
  // already persist to localStorage on every change, so a failed API hit
  // just falls back to whatever was last cached locally.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [hRes, iRes] = await Promise.all([
          domainApi.getHreflang(domainId),
          domainApi.getImageMetadata(domainId),
        ]);
        if (cancelled) return;
        if (hRes.content) {
          try {
            const parsed = JSON.parse(hRes.content);
            if (Array.isArray(parsed)) setHreflangClusters(parsed);
          } catch { /* server blob unparseable — keep localStorage value */ }
        }
        if (iRes.content) {
          try {
            const parsed = JSON.parse(iRes.content);
            if (Array.isArray(parsed)) setImageEntries(parsed);
          } catch { /* same */ }
        }
      } catch {
        // Offline / 5xx — fall back to whatever loadHreflang/loadImage
        // returned from localStorage above. No user-visible error.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  // Debounced write-through to D1. Skips the initial hydration tick.
  const hreflangFirstRender = useRef(true);
  useEffect(() => {
    if (hreflangFirstRender.current) {
      hreflangFirstRender.current = false;
      return;
    }
    const t = setTimeout(() => {
      domainApi.saveHreflang(domainId, JSON.stringify(hreflangClusters)).catch(() => {
        // Save failed (offline / 5xx). localStorage already has the value,
        // so the next mount on this device picks it up. Cross-device sync
        // resumes when the API is reachable again.
      });
    }, 500);
    return () => clearTimeout(t);
  }, [hreflangClusters, domainId]);

  const imagesFirstRender = useRef(true);
  useEffect(() => {
    if (imagesFirstRender.current) {
      imagesFirstRender.current = false;
      return;
    }
    const t = setTimeout(() => {
      domainApi.saveImageMetadata(domainId, JSON.stringify(imageEntries)).catch(() => {
        // Same fail-soft behavior as hreflang above.
      });
    }, 500);
    return () => clearTimeout(t);
  }, [imageEntries, domainId]);

  // Segmentation state
  const [segmentationStrategy, setSegmentationStrategy] = useState<SegmentationStrategy>('sequential');
  const [segmentationDepth, setSegmentationDepth] = useState<number>(1);
  const [segmentationPreview, setSegmentationPreview] = useState<SegmentationPreviewResponse | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Fetch segmentation preview when URLs or settings change
  useEffect(() => {
    if (parsedUrls.length === 0) {
      setSegmentationPreview(null);
      return;
    }

    // Debounce the preview request
    const timeoutId = setTimeout(async () => {
      setIsLoadingPreview(true);
      try {
        const preview = await sitemapApi.previewSegmentation({
          urls: parsedUrls.map(u => ({ loc: u.loc })),
          segmentation: {
            strategy: segmentationStrategy,
            depth: segmentationDepth,
            minGroupSize: 50,
          },
          sitemapSize,
        });
        setSegmentationPreview(preview);
      } catch (err) {
        console.error('Failed to fetch preview:', err);
        setSegmentationPreview(null);
      } finally {
        setIsLoadingPreview(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [parsedUrls, segmentationStrategy, segmentationDepth, sitemapSize]);

  const parseTextToUrls = (text: string): UrlEntry[] => {
    const lines = text.trim().split('\n');
    const urls: UrlEntry[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;

      // Try to parse as URL
      try {
        new URL(trimmed);
        urls.push({ loc: trimmed });
      } catch {
        // Skip invalid URLs
      }
    }

    return urls;
  };

  const parseJsonToUrls = (json: string): UrlEntry[] => {
    const data = JSON.parse(json);
    if (Array.isArray(data)) {
      return data.map((item) => {
        if (typeof item === 'string') {
          return { loc: item };
        }
        return {
          loc: item.loc || item.url,
          lastmod: item.lastmod,
          changefreq: item.changefreq,
          priority: item.priority,
          images: item.images, // Preserve images from JSON
        };
      });
    }
    throw new Error('JSON must be an array of URLs');
  };

  // Parse CSV line handling quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  // Parse enhanced CSV with images (multi-row format)
  const parseCsvWithImages = (csv: string): UrlEntry[] => {
    const lines = csv.trim().split('\n');
    const urlMap = new Map<string, UrlEntry>();
    let currentParentLoc: string | null = null;

    const firstLine = lines[0].toLowerCase();
    const hasHeader = firstLine.includes('type') || firstLine.includes('image_loc');
    const startIndex = hasHeader ? 1 : 0;

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const parts = parseCSVLine(line);
      const type = parts[0]?.toLowerCase().trim();

      if (type === 'url' || (!type && parts[1]?.startsWith('http'))) {
        const loc = type === 'url' ? parts[1]?.trim() : parts[0]?.trim();
        const lastmod = type === 'url' ? parts[2]?.trim() : parts[1]?.trim();

        if (loc && loc.startsWith('http')) {
          urlMap.set(loc, { loc, lastmod: lastmod || undefined, images: [] });
          currentParentLoc = loc;
        }
      } else if (type === 'image') {
        const parentLoc = parts[1]?.trim();
        const imageLoc = parts[3]?.trim();
        const targetLoc = parentLoc || currentParentLoc;

        if (targetLoc && imageLoc && urlMap.has(targetLoc)) {
          const url = urlMap.get(targetLoc)!;
          if (!url.images) url.images = [];
          if (url.images.length < 1000) {
            url.images.push({
              loc: imageLoc,
              title: parts[4]?.trim() || undefined,
              caption: parts[5]?.trim() || undefined,
              geo_location: parts[6]?.trim() || undefined,
              license: parts[7]?.trim() || undefined,
            });
          }
        }
      }
    }

    return Array.from(urlMap.values());
  };

  const parseCsvToUrls = (csv: string): UrlEntry[] => {
    const firstLine = csv.split('\n')[0].toLowerCase();

    // Detect enhanced format with images
    if (firstLine.includes('type') || firstLine.includes('image_loc')) {
      return parseCsvWithImages(csv);
    }

    // Original simple format
    const lines = csv.trim().split('\n');
    const urls: UrlEntry[] = [];

    const hasHeader = firstLine.includes('loc') || firstLine.includes('url');
    const startIndex = hasHeader ? 1 : 0;

    for (let i = startIndex; i < lines.length; i++) {
      const parts = lines[i].split(',').map((p) => p.trim());
      if (parts[0]) {
        urls.push({
          loc: parts[0],
          lastmod: parts[1] || undefined,
          changefreq: parts[2] || undefined,
          priority: parts[3] ? parseFloat(parts[3]) : undefined,
        });
      }
    }

    return urls;
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadedFile(file);
    setError(null);

    try {
      const text = await file.text();
      let urls: UrlEntry[] = [];

      if (file.name.endsWith('.json')) {
        urls = parseJsonToUrls(text);
      } else if (file.name.endsWith('.csv')) {
        urls = parseCsvToUrls(text);
      } else {
        urls = parseTextToUrls(text);
      }

      setParsedUrls(urls);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
      setParsedUrls([]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setTextInput(text);
    setParsedUrls(parseTextToUrls(text));
  };

  const handlePublish = async () => {
    if (parsedUrls.length === 0) {
      setError('No valid URLs to publish');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const segmentation: SegmentationConfig = {
        strategy: segmentationStrategy,
        depth: segmentationDepth,
        minGroupSize: 50,
      };

      // Apply hreflang clusters to matching URLs. Any URL that appears as
      // an entry in a cluster gets the full alternates list attached
      // (Google requires self-referencing alternates, so the cluster
      // membership itself is the right shape — no further transform).
      const hreflangIndex = buildHreflangIndex(hreflangClusters);
      // Image entries are grouped by parent URL and merged with whatever
      // images came in via CSV/JSON. UI-added images come AFTER upload
      // images, capped at 1000 per URL per Google's spec (worker also
      // validates, but bailing client-side keeps the error message tighter).
      const imagesIndex = buildImagesIndex(imageEntries);
      const urlsWithMetadata: UrlEntry[] = parsedUrls.map((u) => {
        const alternates = hreflangIndex.get(u.loc);
        const extraImages = imagesIndex.get(u.loc);
        const merged: UrlEntry = { ...u };
        if (alternates) merged.hreflang = alternates;
        if (extraImages && extraImages.length > 0) {
          const existing = u.images || [];
          merged.images = [...existing, ...extraImages].slice(0, 1000);
        }
        return merged;
      });

      const response = await sitemapApi.generate({
        domain,
        urls: urlsWithMetadata,
        pingGoogle: true,
        sitemapSize,
        segmentation,
      });

      setResult(response);
      onSuccess?.(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate sitemap');
    } finally {
      setIsLoading(false);
    }
  };

  const clearAll = () => {
    setUploadedFile(null);
    setTextInput('');
    setParsedUrls([]);
    setError(null);
    setResult(null);
  };

  return (
    <div className="bg-white rounded-xl border shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Upload URLs</h3>
        <div className="flex rounded-lg border overflow-hidden">
          <button
            onClick={() => setMode('file')}
            className={cn(
              'px-4 py-2 text-sm font-medium transition-colors',
              mode === 'file' ? 'bg-primary text-white' : 'hover:bg-slate-50'
            )}
          >
            File Upload
          </button>
          <button
            onClick={() => setMode('text')}
            className={cn(
              'px-4 py-2 text-sm font-medium transition-colors',
              mode === 'text' ? 'bg-primary text-white' : 'hover:bg-slate-50'
            )}
          >
            Paste URLs
          </button>
        </div>
      </div>

      {/* Prepare Mode Banner */}
      {prepareMode && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />
            <div>
              <p className="font-medium text-blue-800">Prepare your URLs while DNS propagates</p>
              <p className="text-sm text-blue-600 mt-1">
                You can upload and validate your URLs now. Once DNS verification completes, click "Generate Sitemap" to publish.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Result */}
      {result && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-green-800">{result.message}</p>
              <div className="mt-2 space-y-1 text-sm text-green-700">
                <p>
                  Sitemap URL:{' '}
                  <a
                    href={result.sitemapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {result.sitemapUrl}
                  </a>
                </p>
                <p>Total URLs: {formatNumber(result.totalUrls)}</p>
                {result.invalidUrlCount > 0 && (
                  <p className="text-amber-700">
                    {result.invalidUrlCount} invalid URLs were skipped
                  </p>
                )}
              </div>
            </div>
            <button onClick={clearAll} className="p-1 hover:bg-green-100 rounded">
              <X className="w-4 h-4 text-green-600" />
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* File Upload Mode */}
      {mode === 'file' && (
        <div>
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              isDragActive ? 'border-primary bg-primary/5' : 'border-slate-300 hover:border-slate-400'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
            {isDragActive ? (
              <p className="text-primary font-medium">Drop the file here...</p>
            ) : (
              <>
                <p className="font-medium">Drop a file here or click to browse</p>
                <p className="text-sm text-slate-500 mt-1">
                  Supports .json, .csv, or .txt files (up to 50MB)
                </p>
              </>
            )}
          </div>

          {uploadedFile && (
            <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-500" />
              <div className="flex-1">
                <p className="font-medium text-sm">{uploadedFile.name}</p>
                <p className="text-xs text-slate-500">
                  {formatNumber(parsedUrls.length)} URLs parsed
                </p>
              </div>
              <button
                onClick={clearAll}
                className="p-1 hover:bg-slate-200 rounded"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Text Input Mode */}
      {mode === 'text' && (
        <div>
          <textarea
            value={textInput}
            onChange={handleTextChange}
            placeholder="Paste URLs here (one per line)&#10;&#10;https://example.com/page-1&#10;https://example.com/page-2&#10;https://example.com/page-3"
            className="w-full h-48 px-4 py-3 border rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
          {textInput && (
            <p className="text-sm text-slate-500 mt-2">
              {formatNumber(parsedUrls.length)} valid URLs detected
            </p>
          )}
        </div>
      )}

      {/* URL Preview */}
      {parsedUrls.length > 0 && (
        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">Preview (first 5 URLs):</p>
            {parsedUrls.some(u => u.images && u.images.length > 0) && (
              <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                <Image className="w-3 h-3" />
                {parsedUrls.reduce((sum, u) => sum + (u.images?.length || 0), 0)} images
              </span>
            )}
          </div>
          <ul className="text-xs font-mono text-slate-600 space-y-1">
            {parsedUrls.slice(0, 5).map((url, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="truncate flex-1">{url.loc}</span>
                {url.images && url.images.length > 0 && (
                  <span className="text-xs bg-pink-50 text-pink-600 px-1.5 py-0.5 rounded flex-shrink-0">
                    {url.images.length} img
                  </span>
                )}
              </li>
            ))}
            {parsedUrls.length > 5 && (
              <li className="text-slate-400">...and {formatNumber(parsedUrls.length - 5)} more</li>
            )}
          </ul>
        </div>
      )}

      {/* Image Sitemap Hint - shown when URLs exist but no images */}
      {parsedUrls.length > 0 && !parsedUrls.some(u => u.images && u.images.length > 0) && (
        <div className="mt-4 p-3 bg-pink-50 border border-pink-200 rounded-lg">
          <p className="text-sm text-pink-800">
            <span className="font-medium">Pro tip:</span> Add images to your sitemap to boost visibility in Google Images.
            <a
              href="https://developers.google.com/search/docs/crawling-indexing/sitemaps/image-sitemaps"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 underline hover:text-pink-900"
            >
              Learn about image sitemaps
            </a>
          </p>
        </div>
      )}

      {/* Segmentation Strategy Selector */}
      {parsedUrls.length > 0 && (
        <div className="mt-4 p-4 bg-slate-50 rounded-lg">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Segmentation Strategy
          </label>
          <div className="grid grid-cols-2 gap-3">
            {SEGMENTATION_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => setSegmentationStrategy(option.value)}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
                    segmentationStrategy === option.value
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <Icon className={cn(
                    'w-5 h-5 mt-0.5 flex-shrink-0',
                    segmentationStrategy === option.value ? 'text-primary' : 'text-slate-400'
                  )} />
                  <div>
                    <span className="font-medium text-sm block">{option.label}</span>
                    <span className="text-xs text-slate-500">{option.description}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Directory depth selector */}
          {segmentationStrategy === 'directory' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Directory Depth
              </label>
              <div className="flex gap-2">
                {[1, 2, 3].map((depth) => (
                  <button
                    key={depth}
                    onClick={() => setSegmentationDepth(depth)}
                    className={cn(
                      'px-4 py-2 rounded-lg border text-sm font-medium transition-colors',
                      segmentationDepth === depth
                        ? 'border-primary bg-primary text-white'
                        : 'border-slate-200 hover:border-slate-300'
                    )}
                  >
                    Level {depth}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Level 1: /products/ → Level 2: /products/shoes/ → Level 3: /products/shoes/nike/
              </p>
            </div>
          )}

          {/* Segmentation Preview */}
          {isLoadingPreview ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyzing URLs...
            </div>
          ) : segmentationPreview && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Preview</span>
                <span className="text-xs text-slate-500">
                  {segmentationPreview.totalGroups} group{segmentationPreview.totalGroups !== 1 ? 's' : ''} → {segmentationPreview.totalSitemaps} sitemap{segmentationPreview.totalSitemaps !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {segmentationPreview.groups.map((group) => (
                  <div
                    key={group.name}
                    className="p-3 bg-white rounded-lg border border-slate-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FolderTree className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-sm">sitemap-{group.name}.xml</span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {formatNumber(group.urlCount)} URLs
                        {group.estimatedSitemaps > 1 && ` (${group.estimatedSitemaps} files)`}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-slate-400 truncate">
                      {group.sample[0]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Advanced Options */}
      {parsedUrls.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
          >
            <Settings2 className="w-4 h-4" />
            Size Options
            <ChevronDown className={cn('w-4 h-4 transition-transform', showAdvanced && 'rotate-180')} />
          </button>

          {showAdvanced && (
            <div className="mt-3 p-4 bg-slate-50 rounded-lg">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Sitemap Size
              </label>
              <div className="space-y-2">
                {SITEMAP_SIZE_OPTIONS.map((option) => {
                  const estimatedSitemaps = Math.ceil(parsedUrls.length / option.urlsPerSitemap);
                  return (
                    <label
                      key={option.value}
                      className={cn(
                        'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                        sitemapSize === option.value
                          ? 'border-primary bg-primary/5'
                          : 'border-slate-200 hover:border-slate-300'
                      )}
                    >
                      <input
                        type="radio"
                        name="sitemapSize"
                        value={option.value}
                        checked={sitemapSize === option.value}
                        onChange={(e) => setSitemapSize(e.target.value as SitemapSizeTier)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{option.label}</span>
                          <span className="text-xs text-slate-500">
                            {formatNumber(option.urlsPerSitemap)} URLs/sitemap
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{option.description}</p>
                        {parsedUrls.length > 0 && (
                          <p className="text-xs text-slate-400 mt-1">
                            → {estimatedSitemaps} sitemap{estimatedSitemaps > 1 ? 's' : ''} will be created
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Hreflang clusters (optional, for international SEO) */}
      {parsedUrls.length > 0 && (
        <div className="mt-6">
          <HreflangManager
            domain={domain}
            clusters={hreflangClusters}
            onChange={setHreflangClusters}
            knownUrls={parsedUrls.map((u) => u.loc)}
          />
        </div>
      )}

      {/* Image metadata editor (optional, for Google Images) */}
      {parsedUrls.length > 0 && (
        <div className="mt-4">
          <ImageMetadataManager
            domain={domain}
            entries={imageEntries}
            onChange={setImageEntries}
            knownUrls={parsedUrls.map((u) => u.loc)}
          />
        </div>
      )}

      {/* Publish Button */}
      <div className="mt-6">
        <button
          onClick={handlePublish}
          disabled={isLoading || parsedUrls.length === 0 || prepareMode}
          className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Publishing...
            </>
          ) : prepareMode ? (
            <>
              <Clock className="w-4 h-4" />
              Waiting for DNS Verification
            </>
          ) : (
            <>
              Generate Sitemap ({formatNumber(parsedUrls.length)} URLs)
            </>
          )}
        </button>
        {prepareMode && parsedUrls.length > 0 && (
          <p className="text-xs text-center text-slate-500 mt-2">
            {formatNumber(parsedUrls.length)} URLs ready. Complete DNS verification to publish.
          </p>
        )}
      </div>
    </div>
  );
}
